import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type ConsentStatus = 'accepted' | 'declined' | 'undecided';

interface CookieConsentContextType {
  status: ConsentStatus;
  acceptCookies: () => void;
  declineCookies: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const STORAGE_KEY = 'urban_chow_cookie_consent';

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<ConsentStatus>('undecided');

  useEffect(() => {
    const savedStatus = localStorage.getItem(STORAGE_KEY) as ConsentStatus;
    if (savedStatus && ['accepted', 'declined'].includes(savedStatus)) {
      setStatus(savedStatus);
    }
  }, []);

  const acceptCookies = () => {
    setStatus('accepted');
    localStorage.setItem(STORAGE_KEY, 'accepted');
  };

  const declineCookies = () => {
    setStatus('declined');
    localStorage.setItem(STORAGE_KEY, 'declined');
  };

  return (
    <CookieConsentContext.Provider value={{ status, acceptCookies, declineCookies }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
